import Vue from "vue"
import Vuex from "vuex"
import {MessageBox, Message, Notification} from "element-ui"
import moment from "moment"
import {getField, updateField} from "vuex-map-fields"
import hash from "object-hash"
import _ from "lodash"

Vue.use(Vuex)

const apiRoute = (path = "") => `/api/v3/leads-auto-assigner/${path}`

const generateSettingsHash = (hashObject = false, excludeKeysFromHash = []) => {
    const allowedKeys = Object.keys(hashObject).filter(e => !excludeKeysFromHash.includes(e))
    const cleanHashObject = _.pick(hashObject, allowedKeys)
    return hash(cleanHashObject)
}

class CustomMessage {
    constructor(props) {
        const settings = {
            type: "success",
            duration: 3000,
            customClass: "LeadsAutoAssignerMessage",
            message: "A leads auto assigner message..."
        }
        Message(Object.assign(settings, props))
    }
}

export default new Vuex.Store({
    state: {
        savedSettingsHash: null,
        excludeKeysFromHash: ["leads_auto_assign_running"],
        settings: {
            leads_team_auto_assign_running: false,
            leads_auto_assign_running: false,
            leads_broker_delay: null,
            leads_weekly_schedule: [],
            leads_count_type: null,
            leads_default_broker: false,
            leads_recent_broker: false,
            leads_state: [],
            leads_application_type: null,
            leads_type: null,
            leads_assign_training_leads: false,
            leads_teams_enabled_auto_assign: []
        },
        options: {},
        allocation: [],

        newScheduleRow: {dayRange: null, start: null, stop: null},

        unsaved: null,
        loading: false
    },

    getters: {
        unsaved(state) {
            return generateSettingsHash(state.settings, state.excludeKeysFromHash) !== state.savedSettingsHash
        },

        getSettingsField(state) {
            return getField(state.settings)
        },

        wsChannel(state) {
            const tenant_id = Ambition.getGlobal("tenant.tenantId")
            const team_id = Ambition.getGlobal("tenant.currentTeamId")
            const user_id = Ambition.getGlobal("tenant.user.id")
            const team_enabled = Ambition.getGlobal("tenant.teams_enabled")
            return team_enabled && team_id ? `leads_auto_assigner.${tenant_id}.${team_id}` : `leads_auto_assigner.${tenant_id}`
        },

        isTeamChannel() {
            return Ambition.teamsEnabled() && Ambition.currentTeamId() ? true : false
        }
    },

    mutations: {
        // updateField,
        addNewScheduleRow(state, value) {
            const row = Object.assign({}, state.newScheduleRow)
            state.settings.leads_weekly_schedule.push(row)
        },

        updateSchedule(state, schedule) {
            state.settings.leads_weekly_schedule = schedule
        },

        updateLeadsState(state, leadsState) {
            state.settings.leads_state = leadsState
        },

        updateSettingsField(state, value) {
            updateField(state.settings, value)
        },

        showRunningMessage(state) {
            let isRunning
            if (this.getters.isTeamChannel) {
                isRunning = state.settings.leads_team_auto_assign_running
            } else {
                isRunning = state.settings.leads_auto_assign_running
            }
            new CustomMessage({
                type: isRunning ? "error" : "success",
                message: isRunning ? "Leads auto assigner has stopped" : "Leads auto assigner has started"
            })
        },

        createSettingsHash(state, value) {
            state.savedSettingsHash = generateSettingsHash(state.settings, state.excludeKeysFromHash)
        }
    },
    actions: {
        async startAutoAssigner({commit, state, dispatch}, value) {
            const res = await axios
                .get(apiRoute("start"), {
                    params: {team_id: Ambition.getGlobal("tenant.currentTeamId")}
                })
                .then(res => {
                    dispatch("getSettings")
                })
            console.log("startAutoAssigner")
        },

        async stopAutoAssigner({commit, state, dispatch}, value) {
            const res = await axios
                .get(apiRoute("stop"), {
                    params: {team_id: Ambition.getGlobal("tenant.currentTeamId")}
                })
                .then(res => {
                    dispatch("getSettings")
                })
            console.log("stopAutoAssigner")
        },

        async listenForSockets({commit, getters, state}, value) {
            Echo.leave(getters.wsChannel)
            Echo.private(getters.wsChannel).listen(".update", e => {

                const {settings, options, allocation} = e.response

                if (!getters.isTeamChannel && Number(state.settings.leads_auto_assign_running) !== Number(settings.leads_auto_assign_running)) {
                    commit("showRunningMessage")
                    state.settings = Object.assign(state.settings, {leads_auto_assign_running: Number(settings.leads_auto_assign_running)})
                }

                if (getters.isTeamChannel && Number(state.settings.leads_team_auto_assign_running) !== Number(settings.leads_team_auto_assign_running)) {
                    commit("showRunningMessage")
                    state.settings = Object.assign(state.settings, {leads_team_auto_assign_running: Number(settings.leads_team_auto_assign_running)})
                }

                if (!getters.unsaved) {
                    state.settings = settings
                }

                state.options = options
                state.allocation = allocation
            })
        },

        async closeSockets({commit, getters, state}, value) {
            Echo.leave(getters.wsChannel)
        },

        async getSettings({commit, state}, value) {
            state.loading = true

            try {
                const {data, status} = await axios.get(apiRoute("settings"), {
                    params: {team_id: Ambition.getGlobal("tenant.currentTeamId")}
                })
                const {settings, options, allocation} = data
                console.log("getSettings", {data, status})

                state.settings = settings
                state.options = options
                state.allocation = allocation

                state.loading = false

                commit("createSettingsHash")
            } catch (error) {
                new CustomMessage({
                    type: "error",
                    message: "Leads auto assigner encountered an error"
                })
                state.loading = false
                throw error
            }
        },

        async setSettings({commit, state, dispatch}) {
            state.loading = true

            try {
                const {data, status} = await axios.post(apiRoute("settings"), {
                    team_id: Ambition.getGlobal("tenant.currentTeamId"),
                    ...state.settings
                })
                console.log("setSettings", {data, status})
                commit("createSettingsHash")
                // state.loading = false
                dispatch("getSettings")
            } catch (error) {
                // state.unsaved = true
                new CustomMessage({
                    type: "error",
                    message: "Leads auto assigner encountered an error when attempting to update settings"
                })
                state.loading = false
                throw error
            }
        },

        async setBrokerSettings({commit, state, dispatch}, value) {
            state.loading = true

            try {
                console.log("setBrokerSettings", value)
                const {brokerIds, settings} = value
                const {data, status} = await axios.post(apiRoute("broker-settings"), {brokerIds, settings})
                console.log("setBrokerSettings Response", {data, status})
                // state.loading = false
                dispatch("getSettings")
            } catch (error) {
                // state.unsaved = true
                new CustomMessage({
                    type: "error",
                    message: "Leads auto assigner encountered an error when attempting to save broker settings"
                })
                state.loading = false
                throw error
            }
        },

        async submitBrokerAllocationRequest({commit, state, dispatch}, request) {
            state.loading = true

            try {
                const {data, status} = await axios.post(apiRoute("broker-allocation"), request)
                console.log("submitBrokerAllocationRequest", {data, status})
                dispatch("getSettings")
            } catch (error) {
                new CustomMessage({
                    type: "error",
                    message: "Leads auto assigner encountered an error when attempting to submit a lead request"
                })
                state.loading = false
                throw error
            }
        },

        async resetSettings({commit, dispatch, state}) {
            dispatch("getSettings")
        }
    }
})
